import * as THREE from "three";
import { assetUrl } from "../assetVer.js";
import { type Blocker, type WalkBounds } from "./streetScene.js"; // types only (avoids an import cycle)
import { LEATHER_MACHINE_ANCHOR, CRAFTING_TABLE_ANCHOR, CITY_POND } from "@ghostland/shared";
import { dayNight } from "./dayNight.js";
import { makeWaterTexture } from "./kaponTerrain.js";

/**
 * The village — a new live-world ZONE in the pixel pipeline: a small WALLED settlement in
 * a post-apocalyptic concrete world. Low, one-storey textured concrete buildings line a
 * paved street; a perimeter wall rings the village with a sealed metal GATE to the east and
 * the west, each manned by a GUARD who won't let you walk out. The four destinations —
 * a CLOTHING store, a BANK, a CASINO, and the GHOST LAB restaurant — sit along the street with
 * neon signs. You arrive/leave only by the travel pad (the pickup), not the gates.
 */
const PX = 32; // server px per world unit (= 1/U; hardcoded to avoid an import cycle)
const SCENE = "/assets/scene";

/** A pixel texture, optionally tiled. */
function pixTex(path: string, rx = 1, ry = 1): THREE.Texture {
  const t = new THREE.TextureLoader().load(assetUrl(path));
  t.magFilter = t.minFilter = THREE.NearestFilter;
  t.colorSpace = THREE.SRGBColorSpace;
  if (rx !== 1 || ry !== 1) { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(rx, ry); }
  return t;
}

/** A flat, tiled ground texture plane (horizontal). `tileWorld` = world units per tile repeat. */
function groundLayer(group: THREE.Group, path: string, cx: number, cz: number, w: number, d: number, y: number, tileWorld: number): void {
  const tex = pixTex(path, Math.max(1, Math.round(w / tileWorld)), Math.max(1, Math.round(d / tileWorld)));
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshLambertMaterial({ map: tex, transparent: true }));
  m.rotation.x = -Math.PI / 2; m.position.set(cx, y, cz); group.add(m);
}

/** A clean, seamless grass GROUND tile — uniform muted green with sparse single-pixel speckles,
 *  drawn procedurally so it has NO baked-in bushes/blobs (the old grass.png had a bush). */
function grassGroundTex(): THREE.CanvasTexture {
  const S = 32;
  const c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#5c7d40"; ctx.fillRect(0, 0, S, S);
  const speck = ["#54743a", "#647f44", "#4e6e36", "#688449"];
  for (let i = 0; i < 200; i++) {
    ctx.fillStyle = speck[Math.floor(Math.random() * speck.length)];
    ctx.fillRect(Math.floor(Math.random() * S), Math.floor(Math.random() * S), 1, 1);
  }
  const t = new THREE.CanvasTexture(c);
  t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

/** The grass ground plane, using the clean procedural tile above. */
function grassGround(group: THREE.Group, cx: number, cz: number, w: number, d: number, tileWorld: number): void {
  const tex = grassGroundTex();
  tex.repeat.set(Math.max(1, Math.round(w / tileWorld)), Math.max(1, Math.round(d / tileWorld)));
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshLambertMaterial({ map: tex }));
  m.rotation.x = -Math.PI / 2; m.position.set(cx, 0, cz); group.add(m);
}

/** A base-anchored pixel-art billboard prop (tree, bush, bench, house). The PNG is opaque-cropped
 *  on load so its footing sits on the ground and its aspect is correct regardless of canvas padding. */
function cityProp(group: THREE.Group, path: string, x: number, z: number, worldH: number): THREE.Sprite {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, alphaTest: 0.4 }));
  sprite.center.set(0.5, 0); sprite.scale.set(worldH, worldH, 1); sprite.position.set(x, 0.02, z);
  dayNight.registerMat(sprite.material, sprite.position); // city tree darkens/cools at night + lights near lamps
  group.add(sprite);
  const img = new Image();
  img.onload = (): void => {
    const c = document.createElement("canvas"); c.width = img.width; c.height = img.height;
    const ctx = c.getContext("2d", { willReadFrequently: true }); if (!ctx) return;
    ctx.imageSmoothingEnabled = false; ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    let x0 = c.width, x1 = -1, y0 = c.height, y1 = -1;
    for (let yy = 0; yy < c.height; yy++) {
      for (let xx = 0; xx < c.width; xx++) {
        if (data[(yy * c.width + xx) * 4 + 3] > 16) {
          if (xx < x0) x0 = xx; if (xx > x1) x1 = xx; if (yy < y0) y0 = yy; if (yy > y1) y1 = yy;
        }
      }
    }
    if (x1 < 0) return;
    const w = x1 - x0 + 1, h = y1 - y0 + 1;
    const out = document.createElement("canvas"); out.width = w; out.height = h;
    const octx = out.getContext("2d")!; octx.imageSmoothingEnabled = false;
    octx.drawImage(c, x0, y0, w, h, 0, 0, w, h);
    const tex = new THREE.CanvasTexture(out);
    tex.magFilter = tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
    sprite.material.map = tex; sprite.material.needsUpdate = true;
    sprite.scale.set(worldH * (w / h), worldH, 1);
  };
  img.src = assetUrl(path);
  return sprite;
}

/** A textured ATM kiosk at world (x,z): a front panel with a screen recess + keypad + cash slot,
 *  brushed-metal sides, a glowing cyan screen + point light. `rotY` (radians, Y rotation) aims the
 *  front — 0 faces +z; Math.PI/2 faces +x. Shared by the village ATM + the casino ATM. Pass
 *  `colliders`/`blockers` so the solid metal body stops the player (an AABB matching its footprint). */
export function atmMachine(group: THREE.Group, x: number, z: number, rotY = 0,
  colliders?: THREE.Object3D[], blockers?: Blocker[]): void {
  const lm = (file: string): THREE.MeshLambertMaterial => new THREE.MeshLambertMaterial({ map: pixTex(`${SCENE}/city/${file}`) });
  const panel = lm("atm_panel.png"), metal = lm("atm_metal.png");
  // BoxGeometry face order is +x,-x,+y,-y,+z,-z → only the FRONT (+z, index 4) gets the panel.
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.6, 1.4, 0.45), [metal, metal, metal, metal, panel, metal]);
  body.position.set(x, 0.7, z); body.rotation.y = rotY; group.add(body);
  if (colliders) colliders.push(body);
  if (blockers) {
    // axis-aligned footprint of the 0.6×0.45 body, swapped when rotated 90° (rotY ∈ {0, π/2}).
    const hx = Math.abs(Math.cos(rotY)) * 0.3 + Math.abs(Math.sin(rotY)) * 0.225;
    const hz = Math.abs(Math.sin(rotY)) * 0.3 + Math.abs(Math.cos(rotY)) * 0.225;
    blockers.push({ x0: (x - hx) * PX, x1: (x + hx) * PX, y0: (z - hz) * PX, y1: (z + hz) * PX });
  }
  const fx = Math.sin(rotY), fz = Math.cos(rotY); // front-direction unit (rotated +z)
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.38, 0.3),
    new THREE.MeshBasicMaterial({ color: 0x49e0d0 }));
  screen.position.set(x + fx * 0.235, 1.06, z + fz * 0.235); screen.rotation.y = rotY; group.add(screen);
  const glow = new THREE.PointLight(0x49e0d0, 3, 4, 2.0); glow.position.set(x + fx * 0.6, 1.0, z + fz * 0.6); group.add(glow);
}

// ── Apartment homes (physically inside the tenement) ─────────────────────────
/** Storey height of the tenement (world units). */
export const APT_FLOOR_H = 2.9;
/** Walk height (world units) for a tenement storey — 0 for the ground world, the slab top for the
 *  upper floors. Drives both the local player's and remotes' render height (Player.floor). */
export function aptFloorY(floor: number): number {
  return floor >= 2 ? (floor - 1) * APT_FLOOR_H + 0.1 : 0;
}

/** One apartment HOME built into the tenement: a real room at its storey's elevation, sharing the
 *  village world space (its open street window shows the world naturally, both ways). */
export interface AptUnitPlace {
  unit: "2a" | "2b" | "3a" | "3b";
  floor: 2 | 3;                       // storey number → aptFloorY() render height
  bounds: WalkBounds;                 // interior walk box (server px)
  spawn: { x: number; y: number };    // arrive just inside the entry door (north wall)
  door: { x: number; y: number };     // exit trigger (back at the entry door)
  colliders: THREE.Object3D[];        // this storey's walls/slabs, for the trailing camera
  /** Ceiling light — a DOWNWARD spot cone so it can't bleed through the slab into the storey above
   *  (point lights ignore occlusion — no shadows). world3d turns it ON while someone is home. */
  light: THREE.SpotLight;
  /** The window CURTAIN (drawn = visible) — mirrors the synced Building.curtains state. */
  curtain: THREE.Mesh;
  /** Stand here (server px) + E to draw/open the curtains. */
  windowAnchor: { x: number; y: number };
}

/** Curtain fabric tile (procedural, pixelated): warm linen with vertical fold stripes + a darker hem. */
function curtainTex(): THREE.CanvasTexture {
  const S = 32;
  const c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  const folds = ["#8a6a52", "#7d5f49", "#93725a", "#816351"];
  for (let x = 0; x < S; x += 4) { ctx.fillStyle = folds[(x / 4) % folds.length]; ctx.fillRect(x, 0, 4, S); }
  ctx.fillStyle = "#6b5140"; ctx.fillRect(0, S - 3, S, 3); // hem
  return canvasTex(c, 2, 1);
}

/** A nearest-filtered sRGB canvas texture with repeat. */
function canvasTex(c: HTMLCanvasElement, rx: number, ry: number): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(rx, ry);
  return t;
}

/** Clean painted-PLASTER apartment wall tile (procedural, pixelated): a warm off-white with a faint
 *  single-pixel speckle so it reads as pixel art, not a flat fill. Shared by the tenement's real
 *  interiors here and the lobby/halls in streetScene. */
export function aptWallTex(rx: number, ry: number): THREE.CanvasTexture {
  const S = 32;
  const c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#ded8cb"; ctx.fillRect(0, 0, S, S);
  const speck = ["#d6d0c2", "#e4dfd3", "#d1cabb", "#e0dace"];
  for (let i = 0; i < 90; i++) {
    ctx.fillStyle = speck[Math.floor(Math.random() * speck.length)];
    ctx.fillRect(Math.floor(Math.random() * S), Math.floor(Math.random() * S), 1, 1);
  }
  return canvasTex(c, rx, ry);
}

/** SUBTLE polished-CONCRETE floor tile (procedural, pixelated): a flat warm grey with a barely-there
 *  low-contrast speckle and the odd hairline scuff — big floors read calm and clean. */
export function aptConcreteTex(rx: number, ry: number): THREE.CanvasTexture {
  const S = 32;
  const c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#93908a"; ctx.fillRect(0, 0, S, S);
  const speck = ["#8f8c86", "#97948e", "#8b8882", "#999690"]; // ±2–3 tones — barely visible
  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = speck[Math.floor(Math.random() * speck.length)];
    ctx.fillRect(Math.floor(Math.random() * S), Math.floor(Math.random() * S), 1, 1);
  }
  ctx.fillStyle = "#8a8781"; // two faint hairline scuffs
  ctx.fillRect(Math.floor(Math.random() * (S - 6)), Math.floor(Math.random() * S), 5, 1);
  ctx.fillRect(Math.floor(Math.random() * S), Math.floor(Math.random() * (S - 5)), 1, 4);
  return canvasTex(c, rx, ry);
}

export interface VillageBuild {
  bounds: WalkBounds;
  blockers: Blocker[];
  colliders: THREE.Object3D[];
  /** NPC stand positions (world units). Shopkeepers who are now INSIDE their stores are absent.
   *  `winston` stands outside the city's tech tavern and teleports you back to the spawn island;
   *  `miner` stands by the mine area (gives the pickaxe + buys ore). */
  npcSpots: Record<"banker" | "casino" | "guardE" | "guardW" | "winston" | "miner" | "gardener" | "blackmarket" | "marketeer" | "vendor" | "fishmonger", { x: number; z: number; face: number }>;
  /** Open region (world units) where ambient townsfolk wander — the central cross-roads, clear
   *  of the building footprints. */
  npcArea: { x0: number; z0: number; x1: number; z1: number };
  /** Store/building entrance triggers in the village (server px) — stand here + E to enter. */
  storeDoors: {
    threads: { x: number; y: number };
    casino: { x: number; y: number };
    bank: { x: number; y: number };
    furni: { x: number; y: number };
    tackle: { x: number; y: number };
    tavern: { x: number; y: number };
    food: { x: number; y: number };
    ghosts: { x: number; y: number };
    /** The BLACKSMITH shop on the residence row (next to the warehouse). */
    smith: { x: number; y: number };
    /** The apartment tenement's street entrance (→ the lobby zone). */
    apartments: { x: number; y: number };
    /** The ANNEX tenement's street entrance (→ its own lobby zone). */
    annex: { x: number; y: number };
  };
  /** The four real apartment HOMES built into the tenement's upper storeys (bounds/spawn/light…). */
  aptUnits: AptUnitPlace[];
  /** The tenement's whole footprint (server px) — a relogin inside it snaps to the street door. */
  aptFootprint: { x0: number; y0: number; x1: number; y1: number };
  /** The ANNEX block's four homes + footprint (same shape as the first tenement's). */
  anxUnits: AptUnitPlace[];
  anxFootprint: { x0: number; y0: number; x1: number; y1: number };
  /** ATM machine positions (server px) — stand near one + E to deposit/withdraw cash. */
  atms: { x: number; y: number }[];
  /** The warehouse Leather Processing Machine: interaction point (server px) + its mesh (for click
   *  picking). Stand near it + E (or click it) to open the crafting station. */
  leatherMachine: { x: number; y: number; obj: THREE.Object3D };
  /** The warehouse Crafting Table (server px) + its mesh (for click picking). */
  craftingTable: { x: number; y: number; obj: THREE.Object3D };
  /** Where you land when you arrive in the city (via the Ghost Land scroll). */
  enterSpawn: { x: number; y: number };
  /** Advance the animated skybox (drifting clouds). Called each frame by the controller. */
  animateSky: (dt: number) => void;
}

/** A self-lit neon sign plane with text (reads against the gloom). */
function neonSign(text: string, rgb: string, scale = 1): THREE.Mesh {
  const fs = 44, pad = 16;
  const m = document.createElement("canvas").getContext("2d")!;
  m.font = `bold ${fs}px ui-monospace, monospace`;
  const w = Math.ceil(m.measureText(text).width) + pad * 2, h = fs + pad * 2;
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#0b0b10"; ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = rgb; ctx.lineWidth = 4; ctx.strokeRect(3, 3, w - 6, h - 6);
  ctx.font = `bold ${fs}px ui-monospace, monospace`; ctx.textBaseline = "middle"; ctx.textAlign = "center";
  ctx.shadowColor = rgb; ctx.shadowBlur = 16; ctx.fillStyle = rgb;
  ctx.fillText(text, w / 2, h / 2 + 2);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.LinearFilter; tex.colorSpace = THREE.SRGBColorSpace;
  const sh = scale;
  return new THREE.Mesh(new THREE.PlaneGeometry(sh * (w / h), sh), new THREE.MeshBasicMaterial({ map: tex, transparent: true }));
}

/** A one-storey concrete building (textured) + a street-facing neon sign. `signFace` = +1
 *  if the sign is on the +z wall, −1 if on the −z wall. */
function building(
  group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[],
  cx: number, cz: number, w: number, h: number, d: number, texFile: string, tint: number,
  // `y`/`scale` override the sign's height (default 0.66·h — too low on a SHORT building, where
  // it lands on the door) and its size (default 1-unit-tall).
  sign?: { text: string; rgb: string; signFace: 1 | -1; y?: number; scale?: number },
  frontTex?: string,
  // `once` maps every texture 1:1 onto its face (for texture sets authored PER FACE at the face's
  // true aspect — no tiling, no stretching); `backTex` gives the rear face its own artwork.
  opts?: { once?: boolean; backTex?: string },
): void {
  const rx = opts?.once ? 1 : Math.max(1, Math.round(w / 3)), ry = opts?.once ? 1 : Math.max(1, Math.round(h / 3));
  const tex = pixTex(`${SCENE}/village/${texFile}`, rx, ry);
  let mat: THREE.Material | THREE.Material[] = new THREE.MeshLambertMaterial({ map: tex, color: tint });
  if (frontTex) {
    // Distinct texture on the STREET-FACING face (the storefront): +z face is BoxGeometry material
    // index 4, −z face is index 5. Other faces keep the plain side wall.
    const side = new THREE.MeshLambertMaterial({ map: tex, color: tint });
    const front = new THREE.MeshLambertMaterial({ map: pixTex(`${SCENE}/village/${frontTex}`, rx, ry), color: tint });
    mat = [side, side, side, side, side, side];
    const frontIdx = sign && sign.signFace < 0 ? 5 : 4;
    mat[frontIdx] = front;
    if (opts?.backTex) mat[frontIdx === 4 ? 5 : 4] = new THREE.MeshLambertMaterial({ map: pixTex(`${SCENE}/village/${opts.backTex}`, rx, ry), color: tint });
  }
  const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  box.position.set(cx, h / 2, cz);
  group.add(box); colliders.push(box);
  // flat roof slab (slightly darker) for a chunky one-storey silhouette
  const roof = new THREE.Mesh(new THREE.BoxGeometry(w + 0.4, 0.4, d + 0.4), new THREE.MeshLambertMaterial({ color: 0x4a4a50 }));
  roof.position.set(cx, h + 0.2, cz); group.add(roof);
  if (sign) {
    const signY = sign.y ?? h * 0.66;
    const s = neonSign(sign.text, sign.rgb, sign.scale ?? 1);
    s.position.set(cx, signY, cz + sign.signFace * (d / 2 + 0.06));
    if (sign.signFace < 0) s.rotation.y = Math.PI;
    group.add(s);
    const glow = new THREE.PointLight(new THREE.Color(sign.rgb), 6, 12, 2.0);
    glow.position.set(cx, signY, cz + sign.signFace * (d / 2 + 1.2)); group.add(glow);
  }
  blockers.push({ x0: (cx - w / 2) * PX, x1: (cx + w / 2) * PX, y0: (cz - d / 2) * PX, y1: (cz + d / 2) * PX });
}

/** A textured concrete perimeter-wall segment (box) + blocker. Exported so other zones (Kapon's
 *  south edge) can build the SAME city wall. */
export function wallSeg(group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[],
  cx: number, cz: number, w: number, d: number, h = 5): void {
  // Per-face textures so the wall tiles correctly on whichever face is the wide one. A single shared
  // repeat stretches on thin segments — e.g. the N–S gate-flanking walls are thin in x but long in z,
  // so their large visible faces are the ±x faces (spanning the z depth); a repeat derived from the
  // x-width tiles those faces only once → the "one texture smeared over the whole wall" artifact.
  // Give each face pair its own repeat, keyed to that face's real world size (~3 world-units per tile).
  const face = (uw: number, uh: number): THREE.MeshLambertMaterial =>
    new THREE.MeshLambertMaterial({ map: pixTex(`${SCENE}/village/wall.png`, Math.max(1, Math.round(uw / 3)), Math.max(1, Math.round(uh / 3))), color: 0xc4c8cc });
  // BoxGeometry material index order: +x, -x, +y, -y, +z, -z.
  const matX = face(d, h); // ±x faces: depth (z) across × height up
  const matY = face(w, d); // ±y faces (top/bottom): the footprint
  const matZ = face(w, h); // ±z faces: width (x) across × height up
  const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [matX, matX, matY, matY, matZ, matZ]);
  box.position.set(cx, h / 2, cz);
  group.add(box); colliders.push(box);
  blockers.push({ x0: (cx - w / 2) * PX, x1: (cx + w / 2) * PX, y0: (cz - d / 2) * PX, y1: (cz + d / 2) * PX });
}

/** A wrought-iron street lamp: a dark post topped by a warm glowing lantern head (which blooms) +
 *  a warm point light pooling on the street — the city's cinematic night lighting. */
function lampPost(group: THREE.Group, x: number, z: number): void {
  const dark = new THREE.MeshLambertMaterial({ color: 0x24262b });
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.11, 3.2, 6), dark);
  post.position.set(x, 1.6, z); group.add(post);
  const cap = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.3, 6), dark);
  cap.position.set(x, 3.42, z); group.add(cap);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffe2a0 }));
  head.position.set(x, 3.12, z); group.add(head); // bright → blooms
  const light = new THREE.PointLight(0xffce86, 7, 13, 2.0); light.position.set(x, 3.0, z); group.add(light);
}

/** A square 32px canvas, ready for procedural pixel art (NearestFilter, sRGB). */
function pixCanvas(): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const c = document.createElement("canvas"); c.width = c.height = 32;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  return { c, ctx };
}
function asPixTex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A wooden supply-crate texture: plank boards, a frame border, and a diagonal cross-brace. */
function crateTex(): THREE.CanvasTexture {
  const { c, ctx } = pixCanvas();
  ctx.fillStyle = "#7a5230"; ctx.fillRect(0, 0, 32, 32);
  ctx.fillStyle = "#5e3e23"; for (let y = 7; y < 32; y += 8) ctx.fillRect(0, y, 32, 1); // plank seams
  ctx.fillStyle = "#4a301b"; // frame
  ctx.fillRect(0, 0, 32, 3); ctx.fillRect(0, 29, 32, 3); ctx.fillRect(0, 0, 3, 32); ctx.fillRect(29, 0, 3, 32);
  ctx.strokeStyle = "#4a301b"; ctx.lineWidth = 2; ctx.beginPath(); // X brace
  ctx.moveTo(3, 3); ctx.lineTo(29, 29); ctx.moveTo(29, 3); ctx.lineTo(3, 29); ctx.stroke();
  return asPixTex(c);
}

/** A grey electrical-cabinet panel: louvre vents up top and a yellow hazard triangle. */
function electricBoxTex(): THREE.CanvasTexture {
  const { c, ctx } = pixCanvas();
  ctx.fillStyle = "#6b7077"; ctx.fillRect(0, 0, 32, 32);
  ctx.fillStyle = "#54585e"; ctx.fillRect(0, 0, 32, 2); ctx.fillRect(0, 30, 32, 2); ctx.fillRect(0, 0, 2, 32); ctx.fillRect(30, 0, 2, 32);
  ctx.fillStyle = "#3f4348"; for (let y = 5; y < 13; y += 3) ctx.fillRect(6, y, 20, 1); // vents
  // hazard triangle + exclamation
  ctx.fillStyle = "#f2c014"; ctx.beginPath(); ctx.moveTo(16, 16); ctx.lineTo(23, 27); ctx.lineTo(9, 27); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#1a1a1a"; ctx.fillRect(15, 19, 2, 4); ctx.fillRect(15, 24, 2, 2);
  return asPixTex(c);
}

/** A canvas/tarp tent-fabric texture: khaki base with darker vertical seams + speckle. */
function tentTex(): THREE.CanvasTexture {
  const { c, ctx } = pixCanvas();
  ctx.fillStyle = "#7d6b46"; ctx.fillRect(0, 0, 32, 32);
  ctx.fillStyle = "#6a5a3a"; for (let x = 4; x < 32; x += 8) ctx.fillRect(x, 0, 1, 32); // seams
  const speck = ["#857043", "#74633e", "#8b7850"];
  for (let i = 0; i < 70; i++) { ctx.fillStyle = speck[i % speck.length]; ctx.fillRect((i * 7) % 32, (i * 13) % 32, 1, 1); }
  const t = asPixTex(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t;
}

/** A wooden supply crate: a textured cube on the ground, solid (blocks its footprint). */
function crate(group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[], x: number, z: number, size = 1.0): void {
  const box = new THREE.Mesh(new THREE.BoxGeometry(size, size, size), new THREE.MeshLambertMaterial({ map: crateTex() }));
  box.position.set(x, size / 2, z); group.add(box); colliders.push(box);
  blockers.push({ x0: (x - size / 2) * PX, x1: (x + size / 2) * PX, y0: (z - size / 2) * PX, y1: (z + size / 2) * PX });
}

/** A tall grey electrical cabinet (utility box): textured on all four sides + a dark cap. Solid. */
function electricBox(group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[], x: number, z: number): void {
  const w = 0.9, d = 0.6, h = 2.1;
  const panel = new THREE.MeshLambertMaterial({ map: electricBoxTex() });
  const dark = new THREE.MeshLambertMaterial({ color: 0x3a3d42 });
  const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [panel, panel, dark, dark, panel, panel]);
  box.position.set(x, h / 2, z); group.add(box); colliders.push(box);
  const cap = new THREE.Mesh(new THREE.BoxGeometry(w + 0.12, 0.14, d + 0.12), dark);
  cap.position.set(x, h + 0.07, z); group.add(cap);
  blockers.push({ x0: (x - w / 2) * PX, x1: (x + w / 2) * PX, y0: (z - d / 2) * PX, y1: (z + d / 2) * PX });
}

/** A 3D ridge tent: a triangular fabric prism (ridge running along z) with a dark door flap on its
 *  −z (open) end. Textured canvas fabric, solid (blocks its footprint). */
function tent(group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[], x: number, z: number): void {
  const W = 2.4, D = 2.8, H = 1.7;
  const profile = new THREE.Shape();
  profile.moveTo(-W / 2, 0); profile.lineTo(W / 2, 0); profile.lineTo(0, H); profile.lineTo(-W / 2, 0);
  const geo = new THREE.ExtrudeGeometry(profile, { depth: D, bevelEnabled: false });
  geo.translate(0, 0, -D / 2); // centre the prism on z
  const tex = tentTex(); tex.repeat.set(1, 1);
  const body = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ map: tex, side: THREE.DoubleSide }));
  body.position.set(x, 0, z); group.add(body); colliders.push(body);
  // dark triangular door flap on the front (−z) end
  const flap = new THREE.Shape();
  flap.moveTo(-W * 0.24, 0); flap.lineTo(W * 0.24, 0); flap.lineTo(0, H * 0.66); flap.lineTo(-W * 0.24, 0);
  const door = new THREE.Mesh(new THREE.ShapeGeometry(flap), new THREE.MeshLambertMaterial({ color: 0x2a2620, side: THREE.DoubleSide }));
  door.position.set(x, 0, z - D / 2 - 0.01); group.add(door);
  blockers.push({ x0: (x - W / 2) * PX, x1: (x + W / 2) * PX, y0: (z - D / 2) * PX, y1: (z + D / 2) * PX });
}

/** A plain one-storey residence (home): a textured concrete box + flat roof + a dark doorway and a
 *  lit window on its street-facing (−z, north) wall. No neon sign — these are homes, not shops.
 *  Solid (blocks the whole footprint), like the shops. */
function residence(
  group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[],
  cx: number, cz: number, w: number, h: number, d: number, texFile: string, tint: number,
): void {
  const rx = Math.max(1, Math.round(w / 3)), ry = Math.max(1, Math.round(h / 3));
  const tex = pixTex(`${SCENE}/village/${texFile}`, rx, ry);
  const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshLambertMaterial({ map: tex, color: tint }));
  box.position.set(cx, h / 2, cz); group.add(box); colliders.push(box);
  const roof = new THREE.Mesh(new THREE.BoxGeometry(w + 0.4, 0.4, d + 0.4), new THREE.MeshLambertMaterial({ color: 0x44444a }));
  roof.position.set(cx, h + 0.2, cz); group.add(roof);
  const front = cz - d / 2; // street-facing (−z, toward the city) wall
  const door = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 2.0), new THREE.MeshBasicMaterial({ color: 0x16110c }));
  door.position.set(cx - w * 0.18, 1.0, front - 0.03); door.rotation.y = Math.PI; group.add(door);
  const win = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 0.8), new THREE.MeshBasicMaterial({ color: 0xffd98a }));
  win.position.set(cx + w * 0.22, 1.7, front - 0.03); win.rotation.y = Math.PI; group.add(win);
  const glow = new THREE.PointLight(0xffce86, 2.5, 5, 2.0); glow.position.set(cx + w * 0.22, 1.7, front - 0.5); group.add(glow);
  blockers.push({ x0: (cx - w / 2) * PX, x1: (cx + w / 2) * PX, y0: (cz - d / 2) * PX, y1: (cz + d / 2) * PX });
}

/** A low-poly, pixel-textured HONG KONG tenement apartment block — now with REAL upper floors. The
 *  ground storey is a solid body (the lobby lives in its own zone); storeys 2 + 3 are hollow, each
 *  holding TWO real apartment homes (east = A, west = B) at their true elevation, split by a party
 *  wall: plaster interiors, concrete floor slabs, and a big OPEN street window per home so inside
 *  and outside see each other naturally (the homes are part of the world). Rooftop keeps the HK
 *  clutter — water tanks, a stair hut, an antenna. Solid to street players (whole footprint);
 *  players inside a home are clamped to its interior bounds. */
function apartment(
  group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[],
  cx: number, cz: number, w: number, d: number, floors: number, texFile: string, tint: number,
): { door: { x: number; y: number }; units: AptUnitPlace[]; footprint: { x0: number; y0: number; x1: number; y1: number } } {
  const units: AptUnitPlace[] = [];
  const floorH = APT_FLOOR_H;
  const H = floors * floorH; // body height
  const T = 0.35;            // upper-storey wall thickness
  const x0 = cx - w / 2, x1 = cx + w / 2, z0 = cz - d / 2, z1 = cz + d / 2; // z1 = street (south) face
  // — MATERIALS — weathered concrete outside, clean plaster inside. The concrete tiles at a UNIFORM
  // world scale (2.6 units per tile, FRACTIONAL repeats — never rounded to 1) and is anchored to a
  // shared world-space grid via ox/oy (the face's tangent origin), so small segments (sill strips,
  // jambs, lintels) show a slice of the same grid instead of squeezing a whole tile into their box.
  const TILE = 2.6;
  const concrete = (uw: number, uh: number, ox = 0, oy = 0): THREE.MeshLambertMaterial => {
    const t = pixTex(`${SCENE}/village/${texFile}`, uw / TILE, uh / TILE);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.offset.set((((ox / TILE) % 1) + 1) % 1, (((oy / TILE) % 1) + 1) % 1);
    return new THREE.MeshLambertMaterial({ map: t, color: tint });
  };
  const plaster = (uw: number, uh: number): THREE.MeshLambertMaterial =>
    new THREE.MeshLambertMaterial({ map: aptWallTex(uw / 2.2, uh / 2.2) });
  const topMat = new THREE.MeshLambertMaterial({ color: 0x50525a });
  const bandMat = new THREE.MeshLambertMaterial({ color: 0x8a8d94 });
  const ceilMat = new THREE.MeshLambertMaterial({ color: 0xd8d2c6 });
  // BoxGeometry face order: +x, -x, +y, -y, +z, -z. A wall box: ONE weathered outer face, plaster
  // everywhere else (the inner face is a home wall).
  const wallBox = (bw: number, bh: number, bd: number, bx: number, by: number, bz: number, outer: "x+" | "x-" | "z+" | "z-" | "none"): THREE.Mesh => {
    const pl = plaster(Math.max(bw, bd), bh);
    // Anchor the weathered face to the shared facade grid: tangent origin = the segment's low edge
    // along the face (x for the street/back walls, z for the sides) + its base height.
    const tox = outer === "x+" || outer === "x-" ? bz - bd / 2 : bx - bw / 2;
    const ext = concrete(Math.max(bw, bd), bh, tox, by - bh / 2);
    const faces = [outer === "x+" ? ext : pl, outer === "x-" ? ext : pl, pl, pl, outer === "z+" ? ext : pl, outer === "z-" ? ext : pl];
    const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd), faces);
    m.position.set(bx, by, bz); group.add(m);
    return m;
  };

  // — GROUND storey: a solid body (the lobby is its own zone; you never see into it) + plinth.
  const gMatX = concrete(d, floorH, z0, 0), gMatZ = concrete(w, floorH, x0, 0);
  const groundBody = new THREE.Mesh(new THREE.BoxGeometry(w, floorH, d), [gMatX, gMatX, topMat, topMat, gMatZ, gMatZ]);
  groundBody.position.set(cx, floorH / 2, cz); group.add(groundBody); colliders.push(groundBody);
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(w + 0.25, 0.7, d + 0.25), new THREE.MeshLambertMaterial({ color: 0x3d3f45 }));
  plinth.position.set(cx, 0.35, cz); group.add(plinth);

  // — UPPER storeys: real hollow floors, two homes each (east = A, west = B). Inside a home the
  // camera looks SOUTH (entry door on the north wall), so east reads as screen-LEFT — matching the
  // hall, where door A is the left one under the normal north-facing camera.
  const frameMat = new THREE.MeshLambertMaterial({ color: 0xb8bcc2 });
  const OW = 2.6, OH = 1.4; // the street window OPENING per home
  // ONE shared collider list for every home's trailing camera: all storeys' walls + slabs + the
  // roof + the solid ground body. A unit's ceiling is the NEXT storey's slab (or the roof), so
  // per-storey lists would let the camera escape upward through it.
  const interiorColliders: THREE.Object3D[] = [groundBody];
  for (let f = 1; f < floors; f++) {
    const storey = (f + 1) as 2 | 3;
    const fy = f * floorH;            // slab centre height
    const walkY = fy + 0.1;           // slab top = the home's floor
    // Wall height: up to the underside of the NEXT slab — which for the top storey is the roof,
    // whose underside sits 0.1 higher (at H) than an intermediate slab's would, so its walls are
    // 0.1 taller (otherwise a gap shows between the top-floor walls and the roof).
    const wh = floorH - 0.2 + (f === floors - 1 ? 0.1 : 0);
    const wcy = walkY + wh / 2;
    // FLOOR SLAB — concrete walk surface, ceiling-colour underside, band edges (the string course).
    const slabTop = new THREE.MeshLambertMaterial({ map: aptConcreteTex(w / 1.6, d / 1.6) });
    const slab = new THREE.Mesh(new THREE.BoxGeometry(w + 0.3, 0.2, d + 0.3), [bandMat, bandMat, slabTop, ceilMat, bandMat, bandMat]);
    slab.position.set(cx, fy, cz); group.add(slab); colliders.push(slab);
    // PERIMETER walls (weathered outside, plaster inside) + the central party wall.
    interiorColliders.push(slab);
    const add = (m: THREE.Mesh): void => { interiorColliders.push(m); colliders.push(m); };
    add(wallBox(w, wh, T, cx, wcy, z0 + T / 2, "z-"));                 // north (back) wall
    add(wallBox(T, wh, d - 2 * T, x0 + T / 2, wcy, cz, "x-"));         // west wall
    add(wallBox(T, wh, d - 2 * T, x1 - T / 2, wcy, cz, "x+"));         // east wall
    add(wallBox(T, wh, d - 2 * T, cx, wcy, cz, "none"));               // party wall (plaster both sides)
    for (const side of ["a", "b"] as const) {
      const ucx = side === "a" ? cx + w / 4 : cx - w / 4; // home centre x (A east, B west)
      const uw = w / 2;                                   // home exterior width
      // SOUTH (street) wall with the OPEN window: below-sill strip, lintel strip, two jambs.
      // Sill sits LOW (hip height) so someone at the window reads from the street.
      const SILL = 0.6;
      const sillY = walkY + SILL, headY = sillY + OH;
      const sz = z1 - T / 2;
      add(wallBox(uw, SILL, T, ucx, walkY + SILL / 2, sz, "z+"));                            // below the sill
      add(wallBox(uw, wh - SILL - OH, T, ucx, headY + (wh - SILL - OH) / 2, sz, "z+"));      // above the window
      const jw = (uw - OW) / 2;
      add(wallBox(jw, OH, T, ucx - OW / 2 - jw / 2, sillY + OH / 2, sz, "z+"));       // west jamb
      add(wallBox(jw, OH, T, ucx + OW / 2 + jw / 2, sillY + OH / 2, sz, "z+"));       // east jamb
      // window dressing: a BORDER frame (4 strips around the opening — NOT a plate over it, the
      // opening stays see-through) + sill ledge + a barely-there glass sheen.
      const fT = 0.12, fD = 0.1, fz = z1 + 0.02;
      const strip = (bw: number, bh: number, bx: number, by: number): void => {
        const s = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, fD), frameMat);
        s.position.set(bx, by, fz); group.add(s);
      };
      strip(OW + 2 * fT, fT, ucx, headY + fT / 2);              // head (top)
      strip(OW + 2 * fT, fT, ucx, sillY - fT / 2);              // sill bar (bottom)
      strip(fT, OH, ucx - OW / 2 - fT / 2, sillY + OH / 2);     // west side
      strip(fT, OH, ucx + OW / 2 + fT / 2, sillY + OH / 2);     // east side
      const ledge = new THREE.Mesh(new THREE.BoxGeometry(OW + 0.34, 0.1, 0.28), bandMat);
      ledge.position.set(ucx, sillY - 0.17, z1 + 0.06); group.add(ledge);
      const glass = new THREE.Mesh(new THREE.PlaneGeometry(OW, OH),
        new THREE.MeshBasicMaterial({ color: 0x9ec8e0, transparent: true, opacity: 0.08, depthWrite: false, side: THREE.DoubleSide }));
      glass.position.set(ucx, sillY + OH / 2, z1 - T / 2); group.add(glass);
      // an AC unit hung beside the window — keeps the HK facade rhythm
      const ac = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 0.4), new THREE.MeshLambertMaterial({ color: 0xd8dade }));
      ac.position.set(ucx + OW / 2 + 0.75, sillY - 0.3, z1 + 0.2); group.add(ac);
      // ENTRY DOOR visual on the back wall's inner face (the hall teleports you here).
      const dframe = new THREE.Mesh(new THREE.BoxGeometry(1.35, 2.25, 0.08), frameMat);
      dframe.position.set(ucx, walkY + 1.12, z0 + T + 0.05); group.add(dframe);
      const dpanel = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 2.1), new THREE.MeshBasicMaterial({ color: 0x140f0a }));
      dpanel.position.set(ucx, walkY + 1.05, z0 + T + 0.1); group.add(dpanel);
      // CEILING LIGHT — a subtle warm DOWNWARD cone; world3d turns it ON when someone is home. A
      // spot (not a point light) so the glow stays on THIS storey: with no shadow-casting, a point
      // light would shine straight through the slab and light the floor above's walls too.
      const light = new THREE.SpotLight(0xffd9a0, 0, 4.5, Math.PI * 0.42, 0.5, 1.4);
      light.position.set(ucx, walkY + 2.5, cz);
      light.target.position.set(ucx, walkY, cz);
      light.visible = false; group.add(light); group.add(light.target);
      // window CURTAIN — hangs on the room side of the opening; visibility mirrors the synced
      // Building.curtains (drawn closed = visible, blocking the view both ways).
      const curtain = new THREE.Mesh(new THREE.PlaneGeometry(OW + 0.3, OH + 0.35),
        new THREE.MeshLambertMaterial({ map: curtainTex(), side: THREE.DoubleSide }));
      curtain.position.set(ucx, sillY + OH / 2 + 0.05, z1 - T - 0.04);
      curtain.visible = false; group.add(curtain);
      const inX0 = side === "a" ? cx + T / 2 : x0 + T, inX1 = side === "a" ? x1 - T : cx - T / 2;
      units.push({
        unit: `${storey}${side}` as AptUnitPlace["unit"],
        floor: storey,
        bounds: { x0: (inX0 + 0.35) * PX, y0: (z0 + T + 0.5) * PX, x1: (inX1 - 0.35) * PX, y1: (z1 - T - 0.35) * PX },
        spawn: { x: ucx * PX, y: (z0 + T + 0.95) * PX },
        door: { x: ucx * PX, y: (z0 + T + 0.75) * PX },
        colliders: interiorColliders,
        light,
        curtain,
        windowAnchor: { x: ucx * PX, y: (z1 - T - 0.6) * PX },
      });
    }
  }

  // flat roof slab (ceiling-coloured underside — it's the top homes' ceiling) + a low parapet lip
  const roof = new THREE.Mesh(new THREE.BoxGeometry(w, 0.3, d), [bandMat, bandMat, new THREE.MeshLambertMaterial({ color: 0x45474d }), ceilMat, bandMat, bandMat]);
  roof.position.set(cx, H + 0.15, cz); group.add(roof); colliders.push(roof); interiorColliders.push(roof);
  const roofY = H + 0.3; // top surface of the roof slab
  const parapetMat = new THREE.MeshLambertMaterial({ color: 0x7d818a });
  const pph = 0.6, pt = 0.22;
  for (const [bx, bz, bw, bd] of [
    [cx, cz - d / 2 + pt / 2, w, pt], [cx, cz + d / 2 - pt / 2, w, pt],
    [cx - w / 2 + pt / 2, cz, pt, d], [cx + w / 2 - pt / 2, cz, pt, d],
  ] as const) {
    const p = new THREE.Mesh(new THREE.BoxGeometry(bw, pph, bd), parapetMat);
    p.position.set(bx, roofY + pph / 2, bz); group.add(p);
  }
  // rooftop water tanks (rust-red + faded green) on low stands — the HK rooftop signature
  const tank = (tx: number, tz: number, col: number): void => {
    const stand = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.3, 1.0), new THREE.MeshLambertMaterial({ color: 0x33353b }));
    stand.position.set(tx, roofY + 0.15, tz); group.add(stand);
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 1.1, 12), new THREE.MeshLambertMaterial({ color: col }));
    drum.position.set(tx, roofY + 0.85, tz); group.add(drum);
    const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.58, 0.14, 12), new THREE.MeshLambertMaterial({ color: 0x2a2c30 }));
    lid.position.set(tx, roofY + 1.47, tz); group.add(lid);
  };
  tank(cx - w * 0.26, cz + d * 0.22, 0x9c4a35);
  tank(cx - w * 0.02, cz + d * 0.26, 0x6f7a52);
  // rooftop stair hut (roof-access penthouse) + its cap
  const hut = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.5, 1.8), new THREE.MeshLambertMaterial({ color: 0x6b6e75 }));
  hut.position.set(cx + w * 0.22, roofY + 0.75, cz - d * 0.18); group.add(hut);
  const hutCap = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.2, 2.0), new THREE.MeshLambertMaterial({ color: 0x3c3e44 }));
  hutCap.position.set(cx + w * 0.22, roofY + 1.6, cz - d * 0.18); group.add(hutCap);
  // a thin TV antenna
  const darkMetal = new THREE.MeshLambertMaterial({ color: 0x1e2024 });
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 2.2, 6), darkMetal);
  mast.position.set(cx + w * 0.3, roofY + 1.1, cz + d * 0.28); group.add(mast);
  for (const ay of [1.7, 2.0]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.03, 0.03), darkMetal);
    arm.position.set(cx + w * 0.3, roofY + ay, cz + d * 0.28); group.add(arm);
  }
  // — FAKE WINDOWS + AC UNITS — the deterministic lit/dark grid on the faces WITHOUT real openings
  // (north/east/west on every storey; the street face only on the ground storey — its upper floors
  // carry the homes' real open windows built above).
  const litCols = [0xffd98a, 0xffcf76, 0x9ec8e0];            // warm + cool "lights on"
  const darkCols = [0x141820, 0x151a22, 0x1b2028, 0x10141c]; // dark "lights off" panes
  const acMat = new THREE.MeshLambertMaterial({ color: 0xd8dade });
  const acVent = new THREE.MeshLambertMaterial({ color: 0x3a3c42 });
  const ww = 0.9, wh = 1.3;
  const addFace = (nx: number, nz: number, faceW: number, cols: number, opts: { groundOnly?: boolean } = {}): void => {
    const surf = nx !== 0 ? cx + nx * (w / 2) : cz + nz * (d / 2); // wall surface on the normal axis
    const outY = nx !== 0 ? (nx > 0 ? Math.PI / 2 : -Math.PI / 2) : (nz > 0 ? 0 : Math.PI); // face outward
    const lastFloor = opts.groundOnly ? 1 : floors;
    for (let f = 0; f < lastFloor; f++) {
      const yc = f * floorH + floorH * 0.58;
      for (let c = 0; c < cols; c++) {
        const t = ((c + 0.5) / cols - 0.5) * faceW * 0.8; // offset along the face tangent
        const wx = nx !== 0 ? surf : cx + t, wz = nx !== 0 ? cz + t : surf;
        const frame = new THREE.Mesh(new THREE.BoxGeometry(ww + 0.16, wh + 0.16, 0.16), frameMat);
        frame.position.set(wx + nx * 0.06, yc, wz + nz * 0.06); frame.rotation.y = nx !== 0 ? Math.PI / 2 : 0; group.add(frame);
        // most windows dark; only ~1 in 5 has its light on (deterministic per window, no flicker)
        const hsh = f * 7 + c * 13 + (nx !== 0 ? 5 : 0) + (nz < 0 || nx < 0 ? 3 : 0);
        const gc = hsh % 5 === 0 ? litCols[hsh % litCols.length] : darkCols[hsh % darkCols.length];
        const glass = new THREE.Mesh(new THREE.PlaneGeometry(ww, wh), new THREE.MeshBasicMaterial({ color: gc }));
        glass.position.set(wx + nx * 0.145, yc, wz + nz * 0.145); glass.rotation.y = outY; group.add(glass);
        if ((f + c) % 3 === 0 && f > 0) { // AC units, not on the ground floor
          const ac = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 0.4), acMat);
          ac.position.set(wx + nx * 0.2, yc - wh * 0.62, wz + nz * 0.2); ac.rotation.y = nx !== 0 ? Math.PI / 2 : 0; group.add(ac);
          const vent = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.34), acVent);
          vent.position.set(wx + nx * 0.41, yc - wh * 0.62, wz + nz * 0.41); vent.rotation.y = outY; group.add(vent);
        }
      }
    }
  };
  addFace(0, 1, w, 2, { groundOnly: true }); // street face: ground-storey windows flanking the door
  addFace(0, -1, w, 4); // -z (north)
  addFace(1, 0, d, 3);  // +x (east)
  addFace(-1, 0, d, 3); // -x (west)
  // street entrance on the ground floor of the south (+z) face: a framed dark doorway + lintel + step.
  const dz = cz + d / 2;
  const doorFrame = new THREE.Mesh(new THREE.BoxGeometry(1.6, 2.5, 0.2), frameMat);
  doorFrame.position.set(cx, 1.2, dz + 0.04); group.add(doorFrame);
  const doorPanel = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 2.2), new THREE.MeshBasicMaterial({ color: 0x140f0a }));
  doorPanel.position.set(cx, 1.1, dz + 0.16); group.add(doorPanel);
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.28, 0.34), new THREE.MeshLambertMaterial({ color: 0x8a8d94 }));
  lintel.position.set(cx, 2.5, dz + 0.14); group.add(lintel);
  const step = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.22, 0.6), new THREE.MeshLambertMaterial({ color: 0x3d3f45 }));
  step.position.set(cx, 0.11, dz + 0.32); group.add(step);
  const entryGlow = new THREE.PointLight(0xffd9a0, 2.5, 5, 2.0); entryGlow.position.set(cx, 2.2, dz + 0.9); group.add(entryGlow);
  // a drainpipe down the NW corner
  const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, H, 8), new THREE.MeshLambertMaterial({ color: 0x53555b }));
  pipe.position.set(cx - w / 2 - 0.1, H / 2, cz - d / 2 - 0.1); group.add(pipe);
  // a soft warm glow off the street face so lit windows read at night
  const glow = new THREE.PointLight(0xffe1b0, 3, 12, 2.0); glow.position.set(cx, H * 0.6, cz + d / 2 + 1.5); group.add(glow);
  blockers.push({ x0: (cx - w / 2) * PX, x1: (cx + w / 2) * PX, y0: (cz - d / 2) * PX, y1: (cz + d / 2) * PX });
  // the street-entrance trigger point (server px) — stand here + E to enter the lobby
  return {
    door: { x: cx * PX, y: (dz + 0.7) * PX },
    units,
    footprint: { x0: x0 * PX, y0: z0 * PX, x1: x1 * PX, y1: z1 * PX },
  };
}

/** An OPEN warehouse the player can walk into: a back wall + two side walls (metal, blockers) with
 *  a wide open front (north) and a flat roof on pillars. The crafting machine goes inside. */
function warehouse(group: THREE.Group, colliders: THREE.Object3D[], blockers: Blocker[], cx: number, cz: number, w: number, d: number, h = 4): void {
  const wall = (bx: number, bz: number, bw: number, bd: number): void => {
    const tex = pixTex(`${SCENE}/village/metal.png`, Math.max(1, Math.round(bw / 3)), Math.max(1, Math.round(h / 3)));
    const box = new THREE.Mesh(new THREE.BoxGeometry(bw, h, bd), new THREE.MeshLambertMaterial({ map: tex, color: 0xb8bcc2 }));
    box.position.set(bx, h / 2, bz); group.add(box); colliders.push(box);
    blockers.push({ x0: (bx - bw / 2) * PX, x1: (bx + bw / 2) * PX, y0: (bz - bd / 2) * PX, y1: (bz + bd / 2) * PX });
  };
  const T = 0.6;
  wall(cx, cz + d / 2, w + T, T);            // back (south) wall
  wall(cx - w / 2, cz, T, d);                // west wall
  wall(cx + w / 2, cz, T, d);                // east wall
  // No floor: the base grass GROUND plane already extends under the shed, so leaving it exposed makes
  // the warehouse ground identical to the surrounding ground (a separate plane would re-randomise the
  // grass speckles and seam visibly).
  // flat roof on four corner pillars, leaving the north side open to walk in
  const roof = new THREE.Mesh(new THREE.BoxGeometry(w + T, 0.4, d + T), new THREE.MeshLambertMaterial({ color: 0x3c3c42 }));
  roof.position.set(cx, h + 0.2, cz); group.add(roof);
  const dark = new THREE.MeshLambertMaterial({ color: 0x2a2c30 });
  for (const [px2, pz] of [[cx - w / 2, cz - d / 2], [cx + w / 2, cz - d / 2]] as const) {
    const pillar = new THREE.Mesh(new THREE.BoxGeometry(T, h, T), dark); pillar.position.set(px2, h / 2, pz); group.add(pillar);
  }
  // a soft interior light so the open shed reads at night
  const lamp = new THREE.PointLight(0xfff0d0, 4, 10, 2.0); lamp.position.set(cx, h - 0.4, cz); group.add(lamp);
  // OPEN sign over the entrance
  const sign = neonSign("WAREHOUSE", "#9fe0ff");
  sign.position.set(cx, h - 0.2, cz - d / 2 - 0.05); sign.rotation.y = Math.PI; group.add(sign);
}

/** The Leather Processing Machine prop: a riveted metal body with a feed hopper, an output tray, and
 *  a glowing status panel. Returns the group (used as the click-pick target). The spinning gear +
 *  progress bar + SUCCESS/FAIL text are drawn over it by the client CraftController during use. */
function leatherMachine(group: THREE.Group, x: number, z: number): THREE.Object3D {
  const machine = new THREE.Group();
  const metal = (file: string): THREE.MeshLambertMaterial => new THREE.MeshLambertMaterial({ map: pixTex(`${SCENE}/village/${file}`) });
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.3, 1.0), metal("metal.png"));
  body.position.set(0, 0.65, 0); machine.add(body);
  // feed hopper (angled box on top) + a horizontal roller drum
  const hopper = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.5, 0.8), new THREE.MeshLambertMaterial({ color: 0x6a6e74 }));
  hopper.position.set(0, 1.5, 0); hopper.rotation.x = 0.18; machine.add(hopper);
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 1.2, 12), new THREE.MeshLambertMaterial({ color: 0x55585e }));
  drum.rotation.z = Math.PI / 2; drum.position.set(0, 1.0, 0.55); machine.add(drum);
  // output tray (front) + glowing status panel
  const tray = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.12, 0.5), new THREE.MeshLambertMaterial({ color: 0x42454b }));
  tray.position.set(0, 0.45, 0.7); machine.add(tray);
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.28), new THREE.MeshBasicMaterial({ color: 0x74d86a }));
  panel.position.set(0.35, 0.85, 0.51); machine.add(panel);
  const glow = new THREE.PointLight(0x74d86a, 2.5, 4, 2.0); glow.position.set(0.35, 0.9, 0.9); machine.add(glow);
  machine.position.set(x, 0, z);
  group.add(machine);
  return machine;
}

/** A wooden crafting workbench: a plank top on stout legs, with a vice block + a couple of tools.
 *  Returns the group (used as the click-pick target). */
function craftingTable(group: THREE.Group, x: number, z: number): THREE.Object3D {
  const table = new THREE.Group();
  const wood = new THREE.MeshLambertMaterial({ color: 0x6b4a2c });
  const darkWood = new THREE.MeshLambertMaterial({ color: 0x4a3320 });
  const top = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.16, 0.9), wood);
  top.position.set(0, 0.92, 0); table.add(top);
  for (const [lx, lz] of [[-0.7, -0.35], [0.7, -0.35], [-0.7, 0.35], [0.7, 0.35]] as const) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.84, 0.14), darkWood);
    leg.position.set(lx, 0.42, lz); table.add(leg);
  }
  // a metal vice on one corner + a tool block
  const vice = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.2, 0.26), new THREE.MeshLambertMaterial({ color: 0x6a6e74 }));
  vice.position.set(-0.55, 1.1, 0); table.add(vice);
  const block = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.16, 0.4), darkWood);
  block.position.set(0.4, 1.08, 0); table.add(block);
  // a warm work lamp glow so it reads at night
  const glow = new THREE.PointLight(0xffe0a0, 2.0, 4, 2.0); glow.position.set(0, 1.5, 0); table.add(glow);
  table.position.set(x, 0, z);
  group.add(table);
  return table;
}

/** Build the walled village into `group`, centred at world (cx,cz). */
export function buildVillage(group: THREE.Group, cx: number, cz: number): VillageBuild {
  // CITY DISTRICTS (the 2026-07 remap): the E–W main road + the N–S avenue cross at the centre and
  // both run CLEAR (nothing built on asphalt). Around the crossroads sits the COMMERCIAL core
  // (Threads/Winston's/Bank north row; Casino/Ghost Lab south row; Watabaga tucked beside Ghost
  // Lab; Watabaga out east of Ghost Lab toward the gate). The NW quarter is RESIDENTIAL — the
  // tenement stays on its ORIGINAL footprint (saved apartment furniture pins it) with Okka
  // Furniture right across the lane, FACING it. The NE quarter is NATURE — a caustic-water pond,
  // the rock quarry (MINE_AREA) beside it, Kapon trees and open grass. The deep south stays the
  // WORKING row (residences + warehouse/market). Widened (HW 24 → 30 → 32) to give the districts
  // air — streetScene's zoneOf half-extents must match. HW is CAPPED at 32: the detached THREADS
  // interior zone-box starts at tile x=102, so the village box must not cross it.
  const HW = 32, HD = 34;            // interior half-extents (x, z)
  const T = 1.0;                     // wall thickness
  const GATE = 5;                    // gate opening width
  const colliders: THREE.Object3D[] = [];
  const blockers: Blocker[] = [];

  // Lighting + sky are GLOBAL now (the day/night cycle: one player-following sky rig + global lights
  // built in world3d). The city's own lamp posts + neon sign glows (point-lights below) still provide
  // warm pools at night. `animateSky` now drives local per-frame ticks (the pond water shimmer).
  const ticks: ((dt: number) => void)[] = [];
  const animateSky = (dt: number): void => { for (const t of ticks) t(dt); };

  // Layered ground: grass everywhere, an asphalt cross-roads through the centre, and a wide
  // STONE PATH hugging each building row — every store/tavern sits BEHIND this path, with the
  // open grass (the grass-cutting job) out toward the perimeter walls.
  grassGround(group, cx, cz, HW * 2 + 4, HD * 2 + 4, 2.2);
  groundLayer(group, `${SCENE}/city/road.png`, cx, cz, HW * 2, 6, 0.012, 2.2);      // E–W main street
  // N–S avenue — SOUTH arm only: it starts at Winston's back wall (z = cz−11.5) and runs to the
  // south wall. The stretch that used to run on north behind Winston's is gone — the ANNEX
  // tenement + its pavement lane hold that ground now.
  groundLayer(group, `${SCENE}/city/road.png`, cx, cz + (HD - 11.5) / 2, 6, HD + 11.5, 0.013, 2.2);
  // Stone path bands running the full frontage of the north + south building rows (street side),
  // so the buildings read as set back behind a paved walkway. The north row (Winston/Threads/Bank)
  // sits at cz−9, so its path hugs that line; the south row (Casino/Ghost's) sits at cz+7.
  groundLayer(group, `${SCENE}/city/pavement.png`, cx, cz - 5.2, HW * 2 - 2, 2.6, 0.02, 2.0);  // north path
  groundLayer(group, `${SCENE}/city/pavement.png`, cx, cz + 5.2, HW * 2 - 2, 2.6, 0.021, 2.0); // south path
  // RESIDENTIAL pavement: a walkway up from the main road to the first tenement's door (N–S leg at
  // the tenement's centre x), then east along both street faces to the ANNEX's door (E–W leg
  // hugging the fronts at z = cz−14.5 — the doors' trigger points land on it).
  groundLayer(group, `${SCENE}/city/pavement.png`, cx - 16, cz - 9.25, 2.6, 12.5, 0.023, 2.0);  // main road → tenement
  groundLayer(group, `${SCENE}/city/pavement.png`, cx - 8, cz - 14.5, 18.6, 2.6, 0.024, 2.0);   // tenement → annex

  // perimeter walls (north/south solid; east/west split around the gate gap)
  wallSeg(group, colliders, blockers, cx, cz - HD, HW * 2 + T, T);            // north
  wallSeg(group, colliders, blockers, cx, cz + HD, HW * 2 + T, T);            // south
  const sideLen = (HD * 2 - GATE) / 2 + T / 2;
  for (const sx of [cx - HW, cx + HW]) {
    wallSeg(group, colliders, blockers, sx, cz - HD + sideLen / 2, T, sideLen); // upper
    wallSeg(group, colliders, blockers, sx, cz + HD - sideLen / 2, T, sideLen); // lower
  }

  // sealed metal gates in the east + west openings (closed, blocking)
  const gateTex = () => pixTex(`${SCENE}/village/gate.png`);
  for (const sx of [cx - HW, cx + HW]) {
    const gate = new THREE.Mesh(new THREE.BoxGeometry(T * 0.6, 4.4, GATE),
      new THREE.MeshLambertMaterial({ map: gateTex() }));
    gate.position.set(sx, 2.2, cz); group.add(gate); colliders.push(gate);
    blockers.push({ x0: (sx - 0.6) * PX, x1: (sx + 0.6) * PX, y0: (cz - GATE / 2) * PX, y1: (cz + GATE / 2) * PX });
  }

  // NORTH ROW (Winston's level, z = cz−9): THREADS · WINSTON'S · BANK.
  // SOUTH ROW (mine's level, z = cz+7): CASINO · MINE · GHOST LAB.
  building(group, colliders, blockers, cx - 9, cz - 9, 8, 3.6, 5, "tech.png", 0xcfd6e0, { text: "THREADS", rgb: "#ff7ab0", signFace: 1 });
  // THREADS is enterable: a plain dark doorway on its street (+z) face — black like the other stores
  // (no neon door overlay), with a pink glow matching its sign.
  const thX = cx - 9, thFront = cz - 9 + 5 / 2;
  const thOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.5), new THREE.MeshBasicMaterial({ color: 0x0a0e12 }));
  thOpening.position.set(thX, 1.25, thFront + 0.03); group.add(thOpening);
  const thGlow = new THREE.PointLight(0xff7ab0, 5, 8, 2.0); thGlow.position.set(thX, 1.6, thFront + 0.9); group.add(thGlow);
  const threadsDoor = { x: thX * PX, y: (thFront + 0.7) * PX };
  building(group, colliders, blockers, cx + 9, cz - 9, 8, 3.8, 5, "metal.png", 0xffffff, { text: "BANK", rgb: "#7ad0ff", signFace: 1 });
  // BANK is enterable: a lit doorway on its street (+z) face leads to the vault floor.
  const bkX = cx + 9, bkFront = cz - 9 + 5 / 2;
  const bkOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.5), new THREE.MeshBasicMaterial({ color: 0x06121a }));
  bkOpening.position.set(bkX, 1.25, bkFront + 0.03); group.add(bkOpening);
  const bkGlow = new THREE.PointLight(0x7ad0ff, 5, 8, 2.0); bkGlow.position.set(bkX, 1.6, bkFront + 0.9); group.add(bkGlow);
  const bankDoor = { x: bkX * PX, y: (bkFront + 0.7) * PX };
  building(group, colliders, blockers, cx - 9, cz + 9, 8, 3.6, 5, "brick.png", 0xffffff, { text: "CASINO", rgb: "#ffd24a", signFace: -1 });
  // CASINO is enterable: a lit doorway on its street (−z) face leads to the gambling floor.
  const caX = cx - 9, caFront = cz + 9 - 5 / 2;
  const caOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.5), new THREE.MeshBasicMaterial({ color: 0x160d04 }));
  caOpening.position.set(caX, 1.25, caFront - 0.03); caOpening.rotation.y = Math.PI; group.add(caOpening);
  const caGlow = new THREE.PointLight(0xffd24a, 5, 8, 2.0); caGlow.position.set(caX, 1.6, caFront - 0.9); group.add(caGlow);
  const casinoDoor = { x: caX * PX, y: (caFront - 0.7) * PX };
  // GHOST LAB — the restaurant occupies this existing tech-styled storefront by the main road (south row,
  // beside the mine). Now enterable: a lit doorway on its street (−z, north) face like the Casino.
  building(group, colliders, blockers, cx + 9, cz + 9, 8, 3.6, 5, "tech.png", 0xffffff, { text: "GHOST LAB", rgb: "#b89aff", signFace: -1 });
  const ghX = cx + 9, ghFront = cz + 9 - 5 / 2;
  const ghOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.5), new THREE.MeshBasicMaterial({ color: 0x0f0a1a }));
  ghOpening.position.set(ghX, 1.25, ghFront - 0.03); ghOpening.rotation.y = Math.PI; group.add(ghOpening);
  const ghGlow = new THREE.PointLight(0x9a7aff, 5, 8, 2.0); ghGlow.position.set(ghX, 1.6, ghFront - 0.9); group.add(ghGlow);
  const ghostsDoor = { x: ghX * PX, y: (ghFront - 0.7) * PX };

  // The city's WINSTON'S — a modern, tech-styled tavern at the north-centre of the street
  // (taller, cyan neon), a counterpart to the dark desert tavern on the spawn island. Winston
  // himself stands out front; talk to him to teleport back to the spawn island.
  building(group, colliders, blockers, cx, cz - 9, 9, 4.6, 5, "tech.png", 0xcfd6e0, { text: "WINSTON'S", rgb: "#54f0e0", signFace: 1 });
  const wtFront = cz - 9 + 5 / 2;
  const wtOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.6), new THREE.MeshBasicMaterial({ color: 0x07120f }));
  wtOpening.position.set(cx, 1.3, wtFront + 0.03); group.add(wtOpening);
  const wtGlow = new THREE.PointLight(0x54f0e0, 5, 8, 2.0); wtGlow.position.set(cx, 1.6, wtFront + 0.8); group.add(wtGlow);
  // WATABAGA — a fantasy tavern-restaurant on the south row EAST of Ghost Lab, out toward the
  // east gate. Door faces the city (−z, north). Distinct facade texture on the street face; plain
  // timber walls on the other sides. Mirrors RESTAURANT_STREET_DOORS.food + waypoint node 16.
  const foCx = cx + 23, foCz = cz + 9;
  building(group, colliders, blockers, foCx, foCz, 9, 3.8, 5, "food/side.png", 0xffffff, { text: "WATABAGA", rgb: "#ff9a3a", signFace: -1 }, "food/facade.png");
  const foFront = foCz - 5 / 2;
  const foOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.5), new THREE.MeshBasicMaterial({ color: 0x1a0f06 }));
  foOpening.position.set(foCx, 1.25, foFront - 0.03); foOpening.rotation.y = Math.PI; group.add(foOpening);
  const foGlow = new THREE.PointLight(0xff9a3a, 5, 8, 2.0); foGlow.position.set(foCx, 1.6, foFront - 0.9); group.add(foGlow);
  const foodDoor = { x: foCx * PX, y: (foFront - 0.7) * PX };
  // OKKA FURNITURE — the furniture store on the SOUTH ROW, right next to the Casino (across the
  // main street from the tenement's quarter). Door faces the street (−z, north) like its
  // neighbours. A wide brick storefront.
  const fuCx = cx - 20, fuCz = cz + 9;
  building(group, colliders, blockers, fuCx, fuCz, 10, 4.0, 5, "brick.png", 0xd8c8a8, { text: "OKKA FURNITURE", rgb: "#ffb35c", signFace: -1 });
  const fuFront = fuCz - 5 / 2;
  const fuOpening = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 2.5), new THREE.MeshBasicMaterial({ color: 0x1a1206 }));
  fuOpening.position.set(fuCx, 1.25, fuFront - 0.03); fuOpening.rotation.y = Math.PI; group.add(fuOpening);
  const fuGlow = new THREE.PointLight(0xffb35c, 5, 8, 2.0); fuGlow.position.set(fuCx, 1.6, fuFront - 0.9); group.add(fuGlow);
  const furniDoor = { x: fuCx * PX, y: (fuFront - 0.7) * PX };
  // OLD TIMER'S BAITS — the fishing-tackle shack BEHIND Okka (an alley apart). A small weathered
  // wood shack with a PER-FACE Gemini texture set (scripts/style/gen-tackle-facade.py): the box is
  // sized 7 x 3 x 4.5 so each face's aspect EXACTLY matches its artwork (front/back 224x96 = 21:9,
  // sides 144x96 = 3:2) — mapped once, no tiling, no stretching. The storefront faces SOUTH (away
  // from Okka) and is ENTERABLE — the Old Timer trades inside. Ownable ("tackle", seeded to Ghost).
  const tkCx = cx - 20, tkCz = cz + 16;
  // No neon sign — the humble shack announces itself with its storefront alone.
  building(group, colliders, blockers, tkCx, tkCz, 7, 3, 4.5, "tackle/side.png", 0xffffff, undefined, "tackle/facade.png", { once: true, backTex: "tackle/back.png" });
  const tkFront = tkCz + 4.5 / 2;
  // (No door overlay — the generated facade paints its own plank door + lit window.)
  const tkGlow = new THREE.PointLight(0xffd9a0, 3.5, 7, 2.0); tkGlow.position.set(tkCx, 1.5, tkFront + 0.9); group.add(tkGlow); // warm window spill
  const tackleDoor = { x: tkCx * PX, y: (tkFront + 0.7) * PX };
  // (Ghost Lab's storefront + door are built above, on the south row by the main road.)
  const storeDoors: VillageBuild["storeDoors"] = {
    threads: threadsDoor,
    casino: casinoDoor,
    bank: bankDoor,
    furni: furniDoor,
    tackle: tackleDoor,
    tavern: { x: cx * PX, y: (wtFront + 0.7) * PX },
    food: foodDoor,
    ghosts: ghostsDoor,
    apartments: { x: 0, y: 0 }, // filled in below, once the apartment blocks are placed
    annex: { x: 0, y: 0 },
    smith: { x: 0, y: 0 },      // filled in below, at the residence row
  };

  // RESIDENCE ROW + WAREHOUSE — a new street of homes across the deep south of the city (z = cz+28),
  // doors facing north toward the centre. The row ends (east) in an OPEN warehouse the player can walk
  // into; inside sits the Leather Processing Machine. Avoids the central N–S avenue (x 67–73).
  const rowZ = cz + 28; // = 98 when cz = 70
  groundLayer(group, `${SCENE}/city/pavement.png`, cx, rowZ - 5.4, HW * 2 - 2, 2.6, 0.022, 2.0); // walkway in front of the row
  residence(group, colliders, blockers, cx - 18, rowZ, 7, 3.4, 5, "brick.png", 0xffffff);    // x 52
  residence(group, colliders, blockers, cx - 10, rowZ, 7, 3.6, 5, "brick.png", 0xffffff);    // x 60 — brick to match the row
  // BLACKSMITH — the old residence next to the warehouse (x 78) is now Ember's smithy: honest
  // steel (the sword + shield the Black Market used to fence) + the Black Smith teacher. A
  // PER-FACE Gemini texture set (scripts/style/gen-smith-facade.py) in the classic MMORPG
  // smithy code (WoW/FFXIV): soot-stained fieldstone + timber framing, glowing forge window,
  // iron anvil sign, tool rack. The box is sized 7 x 3 x 4.5 so each face's aspect EXACTLY
  // matches its artwork (front/back 224x96 = 21:9, sides 144x96 = 3:2) — mapped once, no
  // tiling. Door faces north toward the centre like the rest of the row (the facade paints
  // its own plank door — no black opening overlay).
  const smCx = cx + 8, smCz = rowZ;
  // Sign: raised to just under the roof + shrunk — at the default 0.66·h it sat square on the
  // painted door of this short (h 3) building.
  building(group, colliders, blockers, smCx, smCz, 7, 3, 4.5, "smith/side.png", 0xffffff, { text: "BLACKSMITH", rgb: "#ff8a3a", signFace: -1, y: 2.62, scale: 0.72 }, "smith/facade.png", { once: true, backTex: "smith/back.png" });
  const smFront = smCz - 4.5 / 2;
  // warm forge-light spill out the front + the furnace-window flicker colour
  const smGlow = new THREE.PointLight(0xff8a3a, 5, 8, 2.0); smGlow.position.set(smCx, 1.5, smFront - 0.9); group.add(smGlow);
  // the smithy CHIMNEY — a soot-stained stone stack off the back corner (the classic smithy
  // silhouette), with a faint ember glow at its mouth.
  const chimTex = pixTex(`${SCENE}/village/smith/stone.png`, 1, 2);
  const chimney = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.8, 0.9), new THREE.MeshLambertMaterial({ map: chimTex }));
  chimney.position.set(smCx - 2.2, 3.4 + 0.9, smCz + 1.4); group.add(chimney);
  const chimCap = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.18, 1.1), new THREE.MeshLambertMaterial({ color: 0x3a3c42 }));
  chimCap.position.set(smCx - 2.2, 3.4 + 1.85, smCz + 1.4); group.add(chimCap);
  const chimGlow = new THREE.PointLight(0xff7a2a, 1.6, 4, 2.0); chimGlow.position.set(smCx - 2.2, 3.4 + 2.1, smCz + 1.4); group.add(chimGlow);
  storeDoors.smith = { x: smCx * PX, y: (smFront - 0.7) * PX };
  // Open warehouse at the east end of the row (centre x 86), entrance facing north.
  const whCx = 86, whCz = rowZ + 1; // room spans z ~96–101; machine sits inside near the back
  warehouse(group, colliders, blockers, whCx, whCz, 8, 6);
  const machineObj = leatherMachine(group, LEATHER_MACHINE_ANCHOR.x / PX, LEATHER_MACHINE_ANCHOR.y / PX);
  const tableObj = craftingTable(group, CRAFTING_TABLE_ANCHOR.x / PX, CRAFTING_TABLE_ANCHOR.y / PX);

  // Mine area — the quarry lives in the NE NATURE quarter, east of the pond (matches the shared
  // MINE_AREA: world x 89–97, z 44–54). No ground patch: the rocks stand directly on the grass —
  // the boulders themselves mark the dig.

  // POND — a small organic caustic-water pool in the NE NATURE quarter, west of the quarry (the
  // Kapon lake's little sibling: same water texture + wobbled outline, scaled down). The outline
  // drives the visual shape AND the shore collision (axis-aligned slabs — the movement system
  // only supports boxes).
  const pondX = CITY_POND.x, pondZ = CITY_POND.z, pondR = CITY_POND.r; // snug to the north wall; FISHABLE (shared constant drives the server's cast check)
  const pondOutline = (a: number): number =>
    pondR * (0.84 + 0.09 * Math.sin(a * 3 + 0.7) + 0.06 * Math.sin(a * 5 + 2.9) + 0.04 * Math.sin(a * 7 + 1.3));
  const pondShape = new THREE.Shape();
  for (let i = 0, NP = 48; i <= NP; i++) {
    const a = (i / NP) * Math.PI * 2, rr = pondOutline(a);
    const lx = Math.cos(a) * rr, ly = Math.sin(a) * rr;
    i === 0 ? pondShape.moveTo(lx, ly) : pondShape.lineTo(lx, ly);
  }
  const pondTex = makeWaterTexture(); pondTex.repeat.set(0.16, 0.16); // ShapeGeometry UVs = world units
  const pondMat = new THREE.MeshStandardMaterial({ map: pondTex, transparent: true, opacity: 0.92, roughness: 0.14, metalness: 0.45 });
  const pondMesh = new THREE.Mesh(new THREE.ShapeGeometry(pondShape, 8), pondMat);
  pondMesh.rotation.x = -Math.PI / 2; pondMesh.position.set(pondX, 0.05, pondZ); group.add(pondMesh);
  let pondT = 0;
  ticks.push((dt) => {
    pondT += dt;
    pondTex.offset.y = (pondTex.offset.y + dt * 0.04) % 1; pondTex.offset.x = Math.sin(pondT * 0.3) * 0.02;
    pondMat.opacity = 0.88 + Math.sin(pondT * 1.3) * 0.05;
  });
  // Shore collision: many thin slabs, each hugging the outline's ACTUAL left/right extent in its
  // z band (the wobbled blob is asymmetric — a symmetric ±max slab overshot the shore), pulled a
  // touch INSIDE the water (INSET) so players can stand right at the visible edge.
  const POND_INSET = 0.25;
  for (let b = 0, NS = 16; b < NS; b++) {
    const z0 = -pondR + 2 * pondR * (b / NS), z1 = -pondR + 2 * pondR * ((b + 1) / NS);
    let xmin = Infinity, xmax = -Infinity;
    for (let i = 0; i < 240; i++) {
      const a = (i / 240) * Math.PI * 2, rr = pondOutline(a), px = Math.cos(a) * rr, pz = Math.sin(a) * rr;
      if (pz >= z0 - 0.15 && pz <= z1 + 0.15) { xmin = Math.min(xmin, px); xmax = Math.max(xmax, px); }
    }
    if (xmax - xmin > 2 * POND_INSET + 0.2) {
      blockers.push({ x0: (pondX + xmin + POND_INSET) * PX, x1: (pondX + xmax - POND_INSET) * PX, y0: (pondZ + z0) * PX, y1: (pondZ + z1) * PX });
    }
  }
  // A couple of Kapon-style trees round out the nature quarter (clear of the shore blockers,
  // the quarry to the east and the bank row below).
  // (tree 1 sits SE of the pond — its old spot west of the water is inside the ANNEX footprint)
  cityProp(group, `${SCENE}/kapon/tree.png`, pondX - 2.0, pondZ + 8.5, 4.4);
  cityProp(group, `${SCENE}/kapon/tree2.png`, pondX + 5.8, pondZ + 6.6, 4.2);

  // ATM — a cyan-screened metal kiosk by the bank. (The casino has its own ATM inside.)
  const atms: { x: number; y: number }[] = [];
  for (const [ax, az] of [[76, 65.5]]) {
    atmMachine(group, ax, az, 0, colliders, blockers);
    atms.push({ x: ax * PX, y: az * PX });
  }

  // BEHIND THE BANK (bank centre cx+9, cz−9 → back/north wall at z = cz−11.5): service clutter —
  // a wooden crate, a tall electrical cabinet, and a 3D ridge tent pitched right up against the
  // bank's back wall and snug beside the electric box. All textured + solid (have collision).
  const bankBackZ = (cz - 9) - 5 / 2; // = 58.5 when cz = 70
  crate(group, colliders, blockers, cx + 6.5, bankBackZ - 1.3);          // crate, west end
  electricBox(group, colliders, blockers, cx + 8.0, bankBackZ - 1.2);    // taller cabinet, beside it
  tent(group, colliders, blockers, cx + 10.2, bankBackZ - 1.5);          // tent, snug to the bank + cabinet

  // APARTMENT BLOCKS — two 3-floor low-poly Hong Kong tenements holding the north RESIDENTIAL
  // strip behind Threads/Winston's. FURNISH_RECTS mirrors both footprints (centres 54,49 and
  // 70,49 · half 7,5); saved furniture is centre-relative, so moving a block means updating its
  // centre in BOTH places together. No signs — residential. The upper storeys HOLD the real homes.
  const apt = apartment(group, colliders, blockers, cx - 16, cz - 21, 14, 10, 3, "hk_wall.png", 0xffffff);
  storeDoors.apartments = apt.door; // stand at the tenement's street door + E to enter the lobby
  // THE ANNEX — the second block, one lane east of the first, on the ground the old N–S avenue's
  // north arm ran through (it now stops at Winston's back wall).
  const anx = apartment(group, colliders, blockers, cx, cz - 21, 14, 10, 3, "hk_wall.png", 0xffffff);
  storeDoors.annex = anx.door;

  // A single tree in the open SW grass. (Cuttable grass tufts are spawned at runtime by the
  // server as part of the grass-cutting job — see GrassController — not decorated here.)
  cityProp(group, `${SCENE}/kapon/tree3.png`, cx - 28.5, cz + 8, 4.4);

  // Street lamps lining the roads — warm pools of light + glowing heads (cinematic, they bloom).
  for (const [lx, lz] of [
    [cx - 7, cz - 7], [cx + 7, cz - 7], [cx - 7, cz + 7], [cx + 7, cz + 7],
    [cx - 17, cz - 6], [cx + 17, cz - 6], [cx - 17, cz + 6], [cx + 17, cz + 6],
    [cx - 8, cz - 13.5],                // the residential pavement lane, between the two tenements
    [cx - 14.5, cz - 10],               // where the tenement walkway leaves the main road
    [cx - 24, cz - 14],                 // the tenement's south lane (residential quarter)
    [cx + 12, cz - 20],                 // the pond path (NE nature quarter, south of the water)
  ] as const) lampPost(group, lx, lz);

  return {
    atms,
    leatherMachine: { x: LEATHER_MACHINE_ANCHOR.x, y: LEATHER_MACHINE_ANCHOR.y, obj: machineObj },
    craftingTable: { x: CRAFTING_TABLE_ANCHOR.x, y: CRAFTING_TABLE_ANCHOR.y, obj: tableObj },
    bounds: { x0: (cx - HW + 1) * PX, y0: (cz - HD + 1) * PX, x1: (cx + HW - 1) * PX, y1: (cz + HD - 1) * PX },
    blockers, colliders,
    npcSpots: {
      banker: { x: cx + 9, z: cz - 3.5, face: Math.PI },
      casino: { x: cx - 9, z: cz + 3.5, face: 0 },
      guardE: { x: cx + HW - 3, z: cz, face: -Math.PI / 2 },  // by the east gate, facing it
      guardW: { x: cx - HW + 3, z: cz, face: Math.PI / 2 },   // by the west gate
      winston: { x: cx, z: cz - 9 + 5 / 2 + 1.3, face: 0 },   // out front of the city tavern
      miner: { x: 93, z: 55.5, face: Math.PI },                // south edge of the NE quarry, facing his rocks
      gardener: { x: cx - 15, z: cz + 4, face: 0 },            // in the SW grass by the lone tree
      blackmarket: { x: cx - 17, z: cz - 2, face: Math.PI / 2 }, // hooded trader by the west wall
      // Outside (north of) the warehouse entrance (warehouse front ≈ z 96, centre x 86), facing the
      // city so approaching players see them.
      marketeer: { x: 83, z: 93, face: Math.PI },
      vendor: { x: 89, z: 93, face: Math.PI },
      // Marla the fish merchant — right beside the OLD TIMER'S BAITS door (shack at cx-20, cz+16;
      // south-facing front at cz+18.25), facing outward like she's minding the stoop.
      fishmonger: { x: cx - 18.2, z: cz + 18.9, face: 0 },
    },
    npcArea: { x0: cx - 18, z0: cz - 2.4, x1: cx + 18, z1: cz + 2.4 }, // along the E–W road

    storeDoors,
    aptUnits: apt.units,
    aptFootprint: apt.footprint,
    anxUnits: anx.units,
    anxFootprint: anx.footprint,
    enterSpawn: { x: cx * PX, y: (cz + 6) * PX },             // arrive in the city centre-south
    animateSky,
  };
}
